import type { Connector } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  ChatTeardropTextIcon,
  DeviceMobileIcon,
  PhoneCallIcon,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { formatPhoneNumberIntl } from "react-phone-number-input";

/**
 * What a demo number actually is, said once, at the top of the page.
 *
 * The row in the table below cannot say it: a `temporary` connector stores the
 * numbers allowed to call *in*, and the number to dial belongs to the demo
 * line's owner — a different tenant's connector. Read as a row, "Numéro
 * temporaire · +33 6 …" looks like the number to call and is the opposite. So
 * the pair is spelled out here — dial *this*, from *these* — with the QR code
 * that saves keying it in, since the phone that has to place the call is rarely
 * the screen showing the number.
 *
 * Rendered only while such a connector exists; it disappears on its own the day
 * the client gets a real line.
 */
export function TemporaryConnectorCallout({
  connector,
}: {
  connector: Connector;
}) {
  const { t } = useTranslation("connectors");

  const callerNumbers = connector.configuration?.caller_numbers || [];
  const voiceNumber = connector.demo_numbers?.voice || "";
  const smsNumber = connector.demo_numbers?.sms || "";

  // Same line for both, nearly always — say it once when it is, and only call
  // out the SMS number when it genuinely differs.
  const smsIsSeparate = Boolean(
    smsNumber &&
    smsNumber !== voiceNumber &&
    connector.capabilities?.includes("sms"),
  );

  // Nothing to dial means the platform runs no demo line right now. The table
  // still lists the connector; a callout headed by an empty number would only
  // raise a question it cannot answer.
  if (!voiceNumber && !smsNumber) return null;

  const dialNumber = voiceNumber || smsNumber;

  return (
    <div className="border-primary/30 bg-primary/5 flex flex-col gap-6 rounded-xl border-2 p-5 sm:flex-row sm:items-center">
      <div className="flex-1 space-y-4">
        <div className="space-y-1">
          <h3 className="flex items-center gap-2 font-semibold tracking-tight">
            <PhoneCallIcon className="text-primary h-5 w-5" weight="fill" />
            {t("temporary.callout.title")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t("temporary.callout.description")}
          </p>
        </div>

        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">
            {t("temporary.callout.dialLabel")}
          </p>
          <a
            href={`tel:${dialNumber}`}
            className="block text-2xl font-bold tracking-tight hover:underline"
          >
            {formatPhoneNumberIntl(dialNumber) || dialNumber}
          </a>
          {smsIsSeparate && (
            <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <ChatTeardropTextIcon className="h-3.5 w-3.5" weight="fill" />
              {t("temporary.callout.smsNumber", {
                number: formatPhoneNumberIntl(smsNumber) || smsNumber,
              })}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">
            {t("temporary.callout.fromLabel")}
          </p>
          {callerNumbers.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t("temporary.callout.noCallerNumbers")}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {callerNumbers.map((number) => (
                <li
                  key={number}
                  className="bg-background flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-sm"
                >
                  <DeviceMobileIcon
                    className="text-muted-foreground h-3.5 w-3.5"
                    weight="fill"
                  />
                  {formatPhoneNumberIntl(number) || number}
                </li>
              ))}
            </ul>
          )}
          <p className="text-muted-foreground text-xs">
            {t("temporary.callout.fromHint")}
          </p>
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 sm:w-44">
        {/* White plate whatever the theme: an inverted QR code is one most
            scanners refuse. */}
        <div className="rounded-lg bg-white p-3">
          <QRCodeSVG
            value={`tel:${dialNumber}`}
            size={128}
            bgColor="#ffffff"
            fgColor="#000000"
            level="M"
          />
        </div>
        <p className="text-muted-foreground text-center text-xs">
          {t("temporary.callout.scan")}
        </p>
      </div>
    </div>
  );
}
